/* =====================================================================
   juizs-sticky-buybar.js — OD-015
   ---------------------------------------------------------------------
   Toont de meelopende koopbalk zodra de echte buy-box uit beeld is.
   De balk heeft geen eigen koop-logica: hij klikt `[data-jph-submit]`
   aan, zodat variant, verkoopplan, aantal en add-ons uit de buy-box
   automatisch meegaan en de AJAX-laag uit juizs-cart.js het overneemt.

   Golf 6a (01-10):
   · Onder 900px kijkt de balk naar de échte knop: zichtbaar zodra die
     boven uit beeld (of onder de vaste header) is, weg zodra hij terug is.
     Boven 900px ongewijzigd: de hele product-hero.
   · Spiegelt ook de variant (2+ varianten) en de gekozen koopvorm.
   · <body> krijgt ruimte onderaan zolang de balk zichtbaar is (alle breedtes,
     sinds 01-10 ook desktop), zodat de footer nooit bedekt raakt.
   · Neemt disabled + tekst van de echte knop over (uitverkocht).

   PORTAL (golf 7d, 14-10): de balk verhuist bij het starten naar <body>,
   net als de ingrediënten-modal (golf 7c). Krijgt de producthero een
   golfrand (CSS-mask), dan zou die de fixed balk afknippen. De knop, de
   prijs en de hero blijven in de sectie; die worden vóór de verhuizing
   opgezocht.
   Theme-editor: bij `shopify:section:unload` van de producthero gaat de
   verhuisde balk weer weg (met observers, listeners en body-ruimte);
   `shopify:section:load` start opnieuw. Zo blijft er nooit een tweede of
   verweesde balk in <body> achter.
   ===================================================================== */
(function () {
  'use strict';

  // Eén keer per pagina. Voert de editor dit script bij het herladen van de
  // sectie opnieuw uit, dan regelen de sectie-events hieronder het al.
  // De naam mag geen element-id zijn (dan is hij al een window-global, 7c).
  if (window.JuizsSbb) return;

  var active = null;

  function init() {
    var bar =
      document.querySelector('[data-juizs-sticky-bar]:not([data-juizs-portal])') ||
      document.querySelector('[data-juizs-sticky-bar]');
    if (!bar) return null;

    // Buy-box en hero blijven in de sectie: opzoeken vóór de verhuizing.
    var submit = document.querySelector('[data-jph-submit]');
    var hero = document.querySelector('.juizs-jph-section');
    var priceSource = document.querySelector('[data-jph-price-main]');
    var priceTarget = bar.querySelector('[data-juizs-sbb-price]');
    var variantTarget = bar.querySelector('[data-juizs-sbb-variant]');
    var planTarget = bar.querySelector('[data-juizs-sbb-plan]');
    var addBtn = bar.querySelector('[data-juizs-sbb-add]');
    var addText = bar.querySelector('[data-juizs-sbb-cta-text]');

    if (!submit) return null;

    var listeners = [];
    var observers = [];
    function on(target, type, fn) {
      target.addEventListener(type, fn);
      listeners.push([target, type, fn]);
    }

    /* ---- Portal naar <body> ---- */
    var section = bar.closest('.shopify-section');
    var sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : null;

    Array.prototype.forEach.call(document.querySelectorAll('[data-juizs-sticky-bar][data-juizs-portal]'), function (stale) {
      if (stale !== bar && stale.parentNode) stale.parentNode.removeChild(stale);
    });
    if (bar.parentNode !== document.body) document.body.appendChild(bar);
    bar.setAttribute('data-juizs-portal', '');

    var mq = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    function isMobile() { return !!mq && mq.matches; }
    var visibleNow = false;

    /* ---- Zichtbaarheid ---- */
    function setVisible(visible) {
      visibleNow = visible;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) {
        bar.removeAttribute('inert');
      } else {
        bar.setAttribute('inert', '');
      }
      // Ruimte onder de pagina zolang de balk er staat (CSS: body.juizs-sbb-open),
      // op alle breedtes: anders bedekt hij onderaan de footer
      if (visible) {
        document.documentElement.style.setProperty('--juizs-sbb-h', bar.offsetHeight + 'px');
      }
      document.body.classList.toggle('juizs-sbb-open', visible);
    }

    var watch = null;
    if ('IntersectionObserver' in window) {
      var observer = null;
      var observed = null;
      watch = function () {
        var mobile = isMobile();
        // 29-07: op desktop de héle product-hero, niet de knop. Anders komt de
        // balk al op zodra de knop net buiten beeld is terwijl je nog in de hero
        // zit. Golf 6a: op mobiel juist de knop zelf (briefing §3.2.6).
        var target = mobile ? submit : (hero || submit);
        if (observer) observer.disconnect();
        observed = target;
        observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.target !== observed) return;
              if (!mobile) { setVisible(!entry.isIntersecting); return; }
              // Mobiel: alleen als de knop bóven het zichtbare deel zit (voorbij
              // gescrold), niet als hij nog onder de vouw ligt.
              var top = entry.rootBounds ? entry.rootBounds.top : 0;
              setVisible(!entry.isIntersecting && entry.boundingClientRect.top < top);
            });
          },
          mobile
            ? { rootMargin: '-64px 0px 0px 0px', threshold: 0 } // vaste header ±61px
            : { rootMargin: '0px 0px -40px 0px', threshold: 0 }
        );
        observer.observe(target);
      };
      watch();
      observers.push({ disconnect: function () { if (observer) observer.disconnect(); } });
      if (mq) {
        if (mq.addEventListener) on(mq, 'change', watch);
        else if (mq.addListener) mq.addListener(watch);
      }
    } else {
      // Terugval: balk gewoon tonen zodra je voorbij de vouw scrolt.
      on(window, 'scroll', function () {
        setVisible(window.scrollY > 600);
      });
    }

    /* ---- Spiegelen: prijs, variant, koopvorm, knopstaat ---- */
    function syncPrice() {
      if (priceSource && priceTarget) priceTarget.textContent = priceSource.textContent.trim();
      if (variantTarget) {
        var checked = document.querySelector('[data-jph-variant] input[type="radio"]:checked');
        var name = checked && checked.closest('[data-jph-variant]').querySelector('.juizs-jph-variant-name');
        if (name) variantTarget.textContent = name.textContent.trim();
      }
      if (planTarget) {
        var tab = document.querySelector('.juizs-jph-tab.is-active');
        planTarget.textContent = tab ? tab.textContent.trim() : '';
        planTarget.hidden = !tab;
      }
    }
    function syncButton() {
      if (!addBtn) return;
      var src = submit.querySelector('.juizs-jph-cta-text');
      var label = src ? src.textContent.trim() : '';
      addBtn.disabled = submit.disabled;
      if (label && addText) addText.textContent = label;
      if (label) addBtn.setAttribute('aria-label', label + ' – ' + (addBtn.getAttribute('data-juizs-sbb-title') || ''));
    }

    if ('MutationObserver' in window) {
      if (priceSource) {
        var priceObserver = new MutationObserver(syncPrice);
        priceObserver.observe(priceSource, {
          childList: true,
          characterData: true,
          subtree: true,
        });
        observers.push(priceObserver);
      }
      var buttonObserver = new MutationObserver(syncButton);
      buttonObserver.observe(submit, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });
      observers.push(buttonObserver);
    }
    on(document, 'juizs:variant-change', function () { syncPrice(); syncButton(); });
    syncPrice();
    syncButton();

    // Hoogte kan wijzigen (lettergrootte, draaien): ruimte onder de pagina bijwerken
    on(window, 'resize', function () {
      if (visibleNow) document.documentElement.style.setProperty('--juizs-sbb-h', bar.offsetHeight + 'px');
    });
    // Ook als de balk zelf van hoogte wisselt. In de editor laadt de stylesheet
    // van de balk na het herladen van de sectie opnieuw; zonder dit bleef de
    // ruimte op de hoogte van de nog ongestileerde balk staan (golf 7d).
    if ('ResizeObserver' in window) {
      var sizeObserver = new ResizeObserver(function () {
        if (visibleNow) document.documentElement.style.setProperty('--juizs-sbb-h', bar.offsetHeight + 'px');
      });
      sizeObserver.observe(bar);
      observers.push(sizeObserver);
    }

    /* ---- Kopen ---- */
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        submit.click();
      });
    }

    /* Opruimen: observers en listeners los, body-ruimte weg, balk uit <body>. */
    function destroy() {
      observers.forEach(function (o) { o.disconnect(); });
      observers = [];
      listeners.forEach(function (l) {
        l[0].removeEventListener(l[1], l[2]);
      });
      listeners = [];
      if (watch && mq && !mq.addEventListener && mq.removeListener) mq.removeListener(watch);
      document.body.classList.remove('juizs-sbb-open');
      document.documentElement.style.removeProperty('--juizs-sbb-h');
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    }

    return { sectionId: sectionId, destroy: destroy };
  }

  function start() {
    // Draait er al een balk en staat er geen nieuwe in de sectie, dan blijft
    // alles zoals het is (anders zou refresh() de enige balk weghalen).
    var fresh = document.querySelector('[data-juizs-sticky-bar]:not([data-juizs-portal])');
    if (active && !fresh) return;
    if (active) active.destroy();
    active = init();
  }

  window.JuizsSbb = { refresh: start };

  /* Theme-editor: de producthero wordt herladen of verwijderd. */
  document.addEventListener('shopify:section:unload', function (e) {
    if (!active || !e.detail || e.detail.sectionId !== active.sectionId) return;
    active.destroy();
    active = null;
  });
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-juizs-sticky-bar]')) start();
  });

  start();
})();
